import { useCallback, useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react'
import { catalog, formatDateEt, formatEt, type OrderSummary } from '@glasses/shared'
import { LoadFailed } from '../components/Banner'
import { loadHistory, type HistoryFilters } from '../lib/data'

/** The API's page size; the pager's "of N" is computed from it. */
const PAGE_SIZE = 25

interface Result {
  /** The page these rows are (the pager labels what is shown, not what is loading). */
  page: number
  items: OrderSummary[]
  total: number
  nextCursor: string | null
}

/** `cursors[i]` opens page i + 1 (page 1 has none). */
interface Position {
  filters: HistoryFilters
  page: number
  cursors: (string | undefined)[]
}

export function OrderHistory({ onOpen }: { onOpen: (orderId: string) => void }) {
  const [text, setText] = useState('')
  const [centerId, setCenterId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [position, setPosition] = useState<Position>({ filters: {}, page: 1, cursors: [undefined] })
  const [result, setResult] = useState<Result | null>(null)
  const [failed, setFailed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [attempt, setAttempt] = useState(0)
  const heading = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    document.title = 'Order history'
    heading.current?.focus()
  }, [])

  useEffect(() => {
    // A newer request (search, page, retry) cancels this one: its late answer is ignored.
    let cancelled = false
    // The previous page stays up while the next loads, as it did in the Blazor page; the pager is
    // disabled meanwhile so its cursor can only come from the page actually shown.
    setFailed(false)
    setLoading(true)
    loadHistory(position.filters, position.cursors[position.page - 1])
      .then((p) => {
        if (!cancelled) setResult({ page: position.page, items: p.data, total: p.total, nextCursor: p.next_cursor })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [position, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  function search(e: FormEvent) {
    e.preventDefault()
    // Searching resets to page 1. The dates are ET calendar days, passed as typed.
    const filters: HistoryFilters = {
      q: text.trim() || undefined,
      center: centerId || undefined,
      from: from || undefined,
      to: to || undefined,
    }
    setPosition({ filters, page: 1, cursors: [undefined] })
  }

  function go(target: number) {
    if (loading) return
    setPosition((p) => {
      const cursors = [...p.cursors]
      if (target > p.page && result?.page === p.page && result.nextCursor) cursors[target - 1] = result.nextCursor
      return { ...p, page: target, cursors }
    })
  }

  const open = (id: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    onOpen(id)
  }

  const pages = result === null ? 1 : Math.max(1, Math.ceil(result.total / PAGE_SIZE))
  const page = result?.page ?? position.page

  return (
    <>
      <h1 ref={heading} tabIndex={-1}>
        Order history
      </h1>

      <form className="search card" onSubmit={search}>
        <label>
          Participant or member number <input id="search-text" value={text} onChange={(e) => setText(e.target.value)} />
        </label>
        <label>
          Center
          <select id="search-center" value={centerId} onChange={(e) => setCenterId(e.target.value)}>
            <option value="">All centers</option>
            {catalog.centers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          From <input id="search-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To <input id="search-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button type="submit" className="btn filled" data-icon="search">
          Search
        </button>
      </form>

      {failed ? (
        <LoadFailed message="Couldn't load orders. Try again." onRetry={retry} />
      ) : result === null ? (
        <p className="loading">Loading…</p>
      ) : result.total === 0 ? (
        <div className="card">
          <p className="empty" data-icon="search_off">
            No orders match.
          </p>
        </div>
      ) : (
        <div className="card table-card">
          <div className="table-scroll">
            <table className="history">
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Order date</th>
                  <th>Participant</th>
                  <th>Member #</th>
                  <th>Center</th>
                  <th>Frames</th>
                  <th>By</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <a href={`/?order=${encodeURIComponent(o.id)}`} onClick={open(o.id)}>
                        {formatEt(o.submitted_at)}
                      </a>
                    </td>
                    <td>{formatDateEt(o.order_date)}</td>
                    <td>{o.participant_name}</td>
                    <td>{o.member_number}</td>
                    <td>{o.center_name}</td>
                    <td className="frames">
                      <ul>
                        {o.frames.map((frame, i) => (
                          <li key={i}>{frame}</li>
                        ))}
                      </ul>
                    </td>
                    <td>{o.submitted_by_name}</td>
                    <td>
                      <span className={`status-chip ${o.email_sent ? 'sent' : 'pending'}`}>
                        {o.email_sent ? 'Sent' : 'Not sent yet'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <nav className="pager">
            <span>
              Page {page} of {pages} ({result.total} orders)
            </span>
            <button type="button" data-icon="chevron_left" disabled={loading || page <= 1} onClick={() => go(page - 1)}>
              Previous
            </button>
            <button
              type="button"
              data-icon="chevron_right"
              disabled={loading || page >= pages || result.nextCursor === null}
              onClick={() => go(page + 1)}
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </>
  )
}
